import { type DragEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { thumbhashToDataUrl } from "@/shared/lib/thumbhash";
import { Spinner } from "./Spinner";
import { Text } from "./Text";
import { useToast } from "./Toast";

const ACCEPT = "image/png,image/jpeg,image/webp";

/** Minimal structural shape the widget renders — any uploaded-file type fits. */
export interface UploaderFile {
  id: string;
  file_name?: string;
  download_url?: string | null;
  thumbhash?: string | null;
}

export interface FileUploaderProps<T extends UploaderFile> {
  label: string;
  files: T[];
  onChange: (files: T[]) => void;
  /** Injected upload behavior (the api-layer `uploadFile`) — shared/ui never fetches. */
  onUpload: (file: File) => Promise<T>;
  max: number;
  disabled?: boolean;
  /** Already-translated aria/labels — shared/ui stays i18n-free. */
  addLabel: string;
  removeLabel: string;
  uploadingLabel: string;
  /** Toast shown when an upload fails. */
  errorMessage: string;
  /**
   * Layout: a square-tile grid (default), a single large drop `card` (one image
   * with an icon + label — e.g. a start/end keyframe), or a chip `bar` (an
   * "Add media" affordance with thumbnails below — e.g. multi-reference).
   */
  variant?: "grid" | "card" | "bar";
  /** Glyph for the empty `card`/`bar` variants. */
  icon?: ReactNode;
  /** When set, the "add" affordance calls this instead of opening the OS file
   *  dialog (e.g. to open a media picker). Drag-drop still uploads directly. */
  onAdd?: () => void;
}

/**
 * Reusable reference-image widget: a tile grid where each "+" runs the injected
 * 3-step upload and yields a file the composer sends on generate. Capped at
 * `max`; uploads in flight show a spinner tile; local object-URL previews give
 * instant feedback. Presentational — every studio shares this one widget.
 */
export function FileUploader<T extends UploaderFile>({
  label,
  files,
  onChange,
  onUpload,
  max,
  disabled,
  addLabel,
  removeLabel,
  uploadingLabel,
  errorMessage,
  variant = "grid",
  icon,
  onAdd,
}: FileUploaderProps<T>) {
  const openAdd = () => (onAdd ? onAdd() : inputRef.current?.click());
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const previews = useRef<Map<string, string>>(new Map());
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const urls = previews.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const remaining = max - files.length - uploading;

  async function onPick(picked: FileList | null) {
    if (!picked || picked.length === 0) return;
    const slots = max - files.length - uploading;
    const batch = Array.from(picked).slice(0, Math.max(0, slots));
    const accepted: T[] = [];
    setUploading((n) => n + batch.length);
    for (const file of batch) {
      const localUrl = URL.createObjectURL(file);
      try {
        const uploaded = await onUpload(file);
        previews.current.set(uploaded.id, localUrl);
        accepted.push(uploaded);
      } catch {
        URL.revokeObjectURL(localUrl);
        toast(errorMessage);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (accepted.length) onChange([...files, ...accepted]);
  }

  const canDrop = remaining > 0 && !disabled;

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    if (canDrop) void onPick(event.dataTransfer.files);
  }

  function removeAt(id: string) {
    const url = previews.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      previews.current.delete(id);
    }
    onChange(files.filter((f) => f.id !== id));
  }

  function previewUrl(file: T): string | undefined {
    return (
      previews.current.get(file.id) ??
      file.download_url ??
      thumbhashToDataUrl(file.thumbhash) ??
      undefined
    );
  }

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      multiple={max > 1}
      className="hidden"
      onChange={(event) => {
        void onPick(event.target.files);
        event.target.value = "";
      }}
    />
  );

  // Single large drop-zone card (icon + label, one image) — e.g. a keyframe slot.
  if (variant === "card") {
    const file = files[0];
    const url = file ? previewUrl(file) : undefined;
    return (
      <div className="relative">
        <button
          type="button"
          onClick={openAdd}
          disabled={disabled || uploading > 0}
          onDragEnter={(event) => {
            event.preventDefault();
            if (canDrop) setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
          }}
          onDrop={onDrop}
          aria-label={addLabel}
          className={cn(
            "relative flex h-28 w-full flex-col items-start justify-between overflow-hidden rounded-lg border border-dashed p-3.5 text-left outline-none transition-colors duration-150 ease-house disabled:pointer-events-none",
            dragging
              ? "border-ink"
              : "border-line-strong hover:border-ink-faint focus-visible:border-ink-faint",
          )}
        >
          {url ? (
            <img
              src={url}
              alt={file?.file_name ?? ""}
              className="absolute inset-0 size-full object-cover"
            />
          ) : uploading > 0 ? (
            <div className="grid size-full place-items-center">
              <Spinner size={18} label={uploadingLabel} />
            </div>
          ) : (
            <>
              <span className="text-ink [&_svg]:size-6">{icon}</span>
              <Text variant="body" as="span" className="text-[14px] font-medium text-ink">
                {label}
              </Text>
            </>
          )}
        </button>
        {file ? (
          <button
            type="button"
            onClick={() => removeAt(file.id)}
            aria-label={removeLabel}
            className="absolute right-2 top-2 grid size-6 place-items-center rounded-full bg-ink/70 text-paper outline-none transition-opacity duration-150 ease-house hover:bg-ink focus-visible:opacity-100 [&_svg]:size-3"
          >
            <CloseIcon />
          </button>
        ) : null}
        {hiddenInput}
      </div>
    );
  }

  // Chip "Add media" bar with thumbnails — e.g. multi-reference uploads.
  if (variant === "bar") {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Text variant="label" as="span">
            {label}
          </Text>
          <Text variant="body" as="span" className="text-[11px] text-ink-faint">
            {files.length}/{max}
          </Text>
        </div>
        {files.length > 0 || uploading > 0 ? (
          <div className="flex flex-wrap gap-2">
            {files.map((file) => {
              const url = previewUrl(file);
              return (
                <div
                  key={file.id}
                  className="group relative size-14 overflow-hidden rounded-md bg-canvas-soft"
                >
                  {url ? (
                    <img src={url} alt={file.file_name ?? ""} className="size-full object-cover" />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => removeAt(file.id)}
                    aria-label={removeLabel}
                    className="absolute right-0.5 top-0.5 grid size-5 place-items-center rounded-full bg-ink/70 text-paper opacity-0 outline-none transition-opacity duration-150 ease-house focus-visible:opacity-100 group-hover:opacity-100 [&_svg]:size-2.5"
                  >
                    <CloseIcon />
                  </button>
                </div>
              );
            })}
            {Array.from({ length: uploading }).map((_, i) => (
              <div
                key={`uploading-${i}`}
                className="grid size-14 place-items-center rounded-md bg-canvas-soft"
              >
                <Spinner size={14} label={uploadingLabel} />
              </div>
            ))}
          </div>
        ) : null}
        {remaining > 0 && !disabled ? (
          <button
            type="button"
            onClick={openAdd}
            aria-label={addLabel}
            className="flex w-full items-center gap-3 rounded-xl border border-dashed border-line-strong bg-canvas-soft px-3.5 py-3 text-left outline-none transition-colors duration-150 ease-house hover:border-ink-faint hover:bg-line-soft"
          >
            <span className="grid size-8 place-items-center rounded-full bg-line-soft text-ink [&_svg]:size-4">
              {icon ?? <PlusIcon />}
            </span>
            <Text variant="body" as="span" className="text-[14px] font-medium text-ink">
              {addLabel}
            </Text>
          </button>
        ) : null}
        {hiddenInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Text variant="label" as="span">
          {label}
        </Text>
        <Text variant="body" as="span" className="text-[11px] text-ink-faint">
          {files.length}/{max}
        </Text>
      </div>

      <div
        onDragEnter={(event) => {
          event.preventDefault();
          if (canDrop) setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={onDrop}
        className={cn(
          "grid grid-cols-4 gap-2 rounded-md transition-[outline-color] duration-150 ease-house",
          dragging ? "outline-2 outline-dashed outline-ink outline-offset-4" : "outline-none",
        )}
      >
        {files.map((file) => {
          const url = previewUrl(file);
          return (
            <div
              key={file.id}
              className="group relative aspect-square overflow-hidden rounded-md bg-canvas-soft"
            >
              {url ? (
                <img src={url} alt={file.file_name ?? ""} className="size-full object-cover" />
              ) : null}
              <button
                type="button"
                onClick={() => removeAt(file.id)}
                aria-label={removeLabel}
                className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-ink/70 text-paper opacity-0 outline-none transition-opacity duration-150 ease-house focus-visible:opacity-100 group-hover:opacity-100 [&_svg]:size-3"
              >
                <CloseIcon />
              </button>
            </div>
          );
        })}

        {Array.from({ length: uploading }).map((_, i) => (
          <div
            key={`uploading-${i}`}
            className="grid aspect-square place-items-center rounded-md bg-canvas-soft"
          >
            <Spinner size={16} label={uploadingLabel} />
          </div>
        ))}

        {remaining > 0 && !disabled ? (
          <button
            type="button"
            onClick={openAdd}
            aria-label={addLabel}
            className="grid aspect-square place-items-center rounded-md border border-dashed border-line-strong text-ink outline-none transition-colors duration-150 ease-house hover:border-ink-faint hover:text-ink focus-visible:border-ink-faint [&_svg]:size-5"
          >
            <PlusIcon />
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={max > 1}
        className="hidden"
        onChange={(event) => {
          void onPick(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
